/**
 * HospitalsDirectoryPage — `/admin/hospitals` (Super Admin)
 * HospitalStaffLoginPage — `/hospital/login`
 * PublicHospitalPage     — `/hospital/:slug`
 */
import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import hospitalApi, { TYPE_LABELS, HOSPITAL_TOKEN_KEY } from '@/lib/hospitalApi';
import api from '@/lib/api';
import afterLogin, { extractRedirect } from '@/lib/afterLogin';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  ArrowLeft, Stethoscope, Plus, MapPin, Phone, Mail, Clock, HeartPulse, Loader2,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/* ── Directory ───────────────────────────────────────────────────── */

export const HospitalsDirectoryPage = () => {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const blank = { name: '', hospital_type: 'clinic', parent_church_id: '', city: '',
    address: '', phone: '', email: '', head_admin_name: '', services: [],
    opening_hours: '', about: '', is_published: false };
  const [form, setForm] = useState(blank);
  const [servicesText, setServicesText] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['hospitals'], queryFn: () => hospitalApi('/hospitals'), retry: false,
  });
  const { data: orgs } = useQuery({
    queryKey: ['org-list-hospitals'],
    queryFn: async () => (await api.get('/admin/organisations')).data,
  });

  const create = useMutation({
    mutationFn: () => hospitalApi('/hospitals', {
      method: 'POST',
      body: JSON.stringify({ ...form,
        services: servicesText.split(',').map((s) => s.trim()).filter(Boolean) }),
    }),
    onSuccess: () => {
      toast.success('Hospital added');
      setForm(blank); setServicesText(''); setOpen(false);
      qc.invalidateQueries({ queryKey: ['hospitals'] });
    },
    onError: (e) => toast.error(e.message),
  });

  return (
    <div className="min-h-screen bg-slate-50" data-testid="hospitals-directory-page">
      <header className="border-b bg-white">
        <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-between gap-3 px-4 py-4">
          <div className="flex items-center gap-3">
            <Link to="/admin" className="text-slate-400 hover:text-slate-700"
                  data-testid="hospitals-back"><ArrowLeft className="h-5 w-5" /></Link>
            <div>
              <h1 className="flex items-center gap-2 text-xl font-bold text-slate-900">
                <Stethoscope className="h-5 w-5 text-rose-600" /> Hospitals & clinics
              </h1>
              <p className="text-xs text-slate-500">
                Institutions in the FWM network, each under its nearest Assembly or Province
              </p>
            </div>
          </div>
          <Button size="sm" onClick={() => setOpen(true)} data-testid="hospital-create-btn">
            <Plus className="mr-1.5 h-3.5 w-3.5" /> Add hospital
          </Button>
        </div>
      </header>

      <main className="mx-auto max-w-5xl space-y-3 px-4 py-6">
        {isLoading ? (
          <p className="py-10 text-center text-sm text-slate-400">
            <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />Loading…
          </p>
        ) : (data?.items || []).length === 0 ? (
          <Card data-testid="hospitals-empty"><CardContent className="py-16 text-center">
            <HeartPulse className="mx-auto mb-3 h-8 w-8 text-slate-300" />
            <p className="text-sm text-slate-600">No hospitals in the network yet.</p>
            <p className="mt-1 text-xs text-slate-400">
              Add one and it appears in the hierarchy under the assembly you choose.
            </p>
          </CardContent></Card>
        ) : data.items.map((h) => (
          <Card key={h.id} data-testid={`hospital-card-${h.id}`}>
            <CardContent className="flex flex-wrap items-center justify-between gap-3 py-4">
              <div>
                <p className="font-semibold text-slate-900">{h.name}</p>
                <p className="text-xs text-slate-500">
                  {TYPE_LABELS[h.hospital_type]}{h.city ? ` · ${h.city}` : ''}
                  {h.parent_name ? ` · under ${h.parent_name}` : ''}
                  {` · ${h.staff_count} staff · ${h.patient_count} patients`}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {h.is_published
                  ? <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-[10px] text-emerald-700">published</Badge>
                  : <Badge variant="outline" className="text-[10px]">draft</Badge>}
                <Button asChild size="sm" data-testid={`hospital-open-${h.id}`}>
                  <Link to={`/admin/hospital/${h.id}`}>Open panel</Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </main>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[88vh] overflow-y-auto" data-testid="hospital-create-dialog">
          <DialogHeader><DialogTitle>Add a hospital</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label>
              <Input value={form.name} data-testid="hospital-name-input"
                     onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Type</Label>
                <select value={form.hospital_type} data-testid="hospital-type-select"
                        onChange={(e) => setForm({ ...form, hospital_type: e.target.value })}
                        className="h-10 w-full rounded-md border border-slate-200 bg-white px-2 text-sm">
                  {(data?.types || ['clinic']).map((t) => (
                    <option key={t} value={t}>{TYPE_LABELS[t] || t}</option>
                  ))}
                </select>
              </div>
              <div><Label>Sits under</Label>
                <select value={form.parent_church_id} data-testid="hospital-parent-select"
                        onChange={(e) => setForm({ ...form, parent_church_id: e.target.value })}
                        className="h-10 w-full rounded-md border border-slate-200 bg-white px-2 text-sm">
                  <option value="">Not linked yet</option>
                  {(orgs || []).map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>City</Label>
                <Input value={form.city} data-testid="hospital-city-input"
                       onChange={(e) => setForm({ ...form, city: e.target.value })} />
              </div>
              <div><Label>Head administrator</Label>
                <Input value={form.head_admin_name} data-testid="hospital-head-input"
                       onChange={(e) => setForm({ ...form, head_admin_name: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Phone</Label>
                <Input value={form.phone} data-testid="hospital-phone-input"
                       onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              </div>
              <div><Label>Email</Label>
                <Input value={form.email} data-testid="hospital-email-input"
                       onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
            </div>
            <div><Label>Services offered</Label>
              <Input value={servicesText} data-testid="hospital-services-input"
                     onChange={(e) => setServicesText(e.target.value)}
                     placeholder="Outpatient, Maternity, Laboratory" />
            </div>
            <div><Label>Opening hours</Label>
              <Input value={form.opening_hours} data-testid="hospital-hours-input"
                     onChange={(e) => setForm({ ...form, opening_hours: e.target.value })}
                     placeholder="Mon–Fri 08:00–17:00, Sat 08:00–12:00" />
            </div>
            <div><Label>About</Label>
              <Textarea rows={3} value={form.about} data-testid="hospital-about-input"
                        onChange={(e) => setForm({ ...form, about: e.target.value })} />
            </div>
            <label className="flex cursor-pointer items-center gap-2">
              <input type="checkbox" checked={form.is_published} data-testid="hospital-publish-checkbox"
                     onChange={(e) => setForm({ ...form, is_published: e.target.checked })} />
              <span className="text-xs text-slate-600">Publish the public page straight away</span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={() => create.mutate()} disabled={create.isPending || !form.name.trim()}
                    data-testid="hospital-create-save-btn">Add</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

/* ── Staff login ─────────────────────────────────────────────────── */

export const HospitalStaffLoginPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await fetch(`${API_URL}/api/hospital/auth/login`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Sign in failed');
      localStorage.setItem(HOSPITAL_TOKEN_KEY, data.access_token);
      localStorage.setItem('hospital_staff', JSON.stringify(data.staff || {}));
      toast.success(`Welcome, ${data.staff?.name || 'colleague'}`);
      // The landing resolver stays in charge; the hospital's own panel is
      // passed as the requested destination.
      await afterLogin({
        navigate, userType: 'hospital_staff', tenantId: null,
        redirect: extractRedirect() || data.landing, token: data.access_token,
      });
    } catch (err) {
      toast.error(err.message);
    }
    setBusy(false);
  };

  return (
    <div className="grid min-h-screen place-items-center bg-slate-950 px-6"
         data-testid="hospital-login-page">
      <form onSubmit={submit} className="w-full max-w-sm rounded-2xl bg-slate-900 p-8">
        <HeartPulse className="mb-4 h-8 w-8 text-rose-500" />
        <h1 className="text-xl font-bold text-white">Hospital staff sign in</h1>
        <p className="mt-1 text-xs text-slate-400">
          Doctors, nurses, reception, pharmacy and lab staff.
        </p>
        <div className="mt-6 space-y-3">
          <div>
            <Label className="text-slate-300">Email</Label>
            <Input type="email" value={email} required data-testid="hospital-login-email"
                   onChange={(e) => setEmail(e.target.value)}
                   className="border-slate-700 bg-slate-800 text-white" />
          </div>
          <div>
            <Label className="text-slate-300">Password</Label>
            <Input type="password" value={password} required data-testid="hospital-login-password"
                   onChange={(e) => setPassword(e.target.value)}
                   className="border-slate-700 bg-slate-800 text-white" />
          </div>
          <Button type="submit" disabled={busy} className="w-full bg-rose-600 hover:bg-rose-700"
                  data-testid="hospital-login-submit">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Sign in
          </Button>
        </div>
      </form>
    </div>
  );
};

/* ── Public hospital page ────────────────────────────────────────── */

export const PublicHospitalPage = () => {
  const { slug } = useParams();
  const [form, setForm] = useState({ name: '', contact: '', message: '' });
  const [sent, setSent] = useState('');

  const { data, error } = useQuery({
    queryKey: ['public-hospital', slug],
    queryFn: async () => {
      const r = await fetch(`${API_URL}/api/public/hospitals/${slug}`);
      if (!r.ok) throw new Error('Not found');
      return r.json();
    },
    retry: false,
  });

  const send = useMutation({
    mutationFn: async () => {
      const r = await fetch(`${API_URL}/api/public/hospitals/${slug}/enquiries`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const body = await r.json();
      if (!r.ok) throw new Error(body.detail || 'Could not send');
      return body;
    },
    onSuccess: (res) => { setSent(res.message); setForm({ name: '', contact: '', message: '' }); },
    onError: (e) => toast.error(e.message),
  });

  if (error) {
    return (
      <div className="grid min-h-screen place-items-center bg-slate-50" data-testid="public-hospital-error">
        <p className="text-sm text-slate-500">This hospital does not have a public page.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="public-hospital-page">
      <header className="bg-slate-900 px-6 py-16 text-white">
        <div className="mx-auto max-w-3xl">
          <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-rose-400">
            {TYPE_LABELS[data?.hospital_type] || 'Healthcare'}
          </p>
          <h1 className="mt-2 text-3xl font-bold sm:text-4xl" data-testid="public-hospital-name">
            {data?.name || slug}
          </h1>
          {data?.about && <p className="mt-4 max-w-xl text-sm leading-relaxed text-slate-300">{data.about}</p>}
        </div>
      </header>

      <main className="mx-auto max-w-3xl space-y-10 px-6 py-12">
        <section className="grid gap-4 sm:grid-cols-2" data-testid="public-hospital-details">
          {[[MapPin, 'Where to find us', [data?.address, data?.city].filter(Boolean).join(', ')],
            [Clock, 'Opening hours', data?.opening_hours],
            [Phone, 'Phone', data?.phone],
            [Mail, 'Email', data?.email]].map(([Icon, label, value]) => value ? (
              <div key={label} className="rounded-xl border border-slate-200 p-4">
                <p className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                  <Icon className="h-3.5 w-3.5" /> {label}
                </p>
                <p className="mt-1 text-sm text-slate-700">{value}</p>
              </div>
            ) : null)}
        </section>

        {(data?.services || []).length > 0 && (
          <section data-testid="public-hospital-services">
            <h2 className="text-lg font-semibold text-slate-900">Services we offer</h2>
            <div className="mt-3 flex flex-wrap gap-2">
              {data.services.map((s) => (
                <span key={s} className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs text-slate-700">
                  {s}
                </span>
              ))}
            </div>
          </section>
        )}

        <section data-testid="public-hospital-contact-form">
          <h2 className="text-lg font-semibold text-slate-900">Get in touch</h2>
          {sent ? (
            <p className="mt-3 rounded-lg bg-emerald-50 p-4 text-sm text-emerald-800"
               data-testid="public-hospital-sent">{sent}</p>
          ) : (
            <div className="mt-3 space-y-3">
              <div className="grid gap-3 sm:grid-cols-2">
                <Input placeholder="Your name" value={form.name} data-testid="hospital-contact-name"
                       onChange={(e) => setForm({ ...form, name: e.target.value })} />
                <Input placeholder="Phone or email" value={form.contact} data-testid="hospital-contact-contact"
                       onChange={(e) => setForm({ ...form, contact: e.target.value })} />
              </div>
              <Textarea rows={4} placeholder="How can we help?" value={form.message}
                        data-testid="hospital-contact-message"
                        onChange={(e) => setForm({ ...form, message: e.target.value })} />
              <Button onClick={() => send.mutate()} data-testid="hospital-contact-submit"
                      disabled={send.isPending || form.name.trim().length < 2
                        || form.contact.trim().length < 3 || form.message.trim().length < 5}>
                Send enquiry
              </Button>
            </div>
          )}
        </section>
      </main>
    </div>
  );
};

export default HospitalsDirectoryPage;
